import React, { useState, useEffect } from 'react';
import { adminAPI, Application } from '../../services/api';
import DocumentReview from './DocumentReview';
import {
  displayDocumentName,
  formatMimeLabel,
} from '../../utils/documentDisplay';
import './ApplicationsQueue.css';

const ApplicationsQueue: React.FC = () => {
  const [selectedApplication, setSelectedApplication] = useState<Application | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'date' | 'amount' | 'risk'>('date');
  const [applications, setApplications] = useState<Application[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [actionType, setActionType] = useState<'approve' | 'reject' | 'request_info'>('approve');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDocumentReview, setShowDocumentReview] = useState(false);
  const [applicationDocuments, setApplicationDocuments] = useState<any[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [showDisburseModal, setShowDisburseModal] = useState(false);
  const [disburseApp, setDisburseApp] = useState<Application | null>(null);
  const [disburseReference, setDisburseReference] = useState('');
  const [disburseNotes, setDisburseNotes] = useState('');
  const [disburseConfirmed, setDisburseConfirmed] = useState(false);
  const [disbursing, setDisbursing] = useState(false);

  // Fetch applications from API
  useEffect(() => {
    const fetchApplications = async () => {
      try {
        setLoading(true);
        const response = await adminAPI.getApplicationsQueue({
          status: filterStatus === 'all' ? undefined : filterStatus,
          page: 1,
          limit: 50
        });
        
        if (response.success) {
          setApplications(response.data.applications);
        } else {
          setError('Failed to fetch applications');
        }
      } catch (err) {
        console.error('Error fetching applications:', err);
        setError('Failed to fetch applications');
      } finally {
        setLoading(false);
      }
    };

    fetchApplications();
  }, [filterStatus]);

  // Processing functions
  const handleApplicationAction = (action: 'approve' | 'reject' | 'request_info', applicationId: string) => {
    setActionType(action);
    const app = applications.find(app => app.id === applicationId);
    setSelectedApplication(app || null);
    setShowCommentModal(true);
  };

  // Fetch documents and risk assessment when application is selected
  useEffect(() => {
    const fetchApplicationDetails = async () => {
      if (!selectedApplication) {
        setApplicationDocuments([]);
        return;
      }

      try {
        setLoadingDocuments(true);
        const response = await adminAPI.getApplicationDetails(selectedApplication.id);
        
        if (response.success && response.data) {
          const data = response.data as any;
          const docs = data.documents || [];
          setApplicationDocuments(docs);
          
          // Update selected application with risk assessment if available
          if (data.riskAssessment) {
            setSelectedApplication(prev => prev ? {
              ...prev,
              riskAssessment: data.riskAssessment,
              riskScore: data.riskAssessment.overallScore
            } : null);
          }

          setSelectedApplication(prev => prev ? {
            ...prev,
            saIdNumber: data.sa_id_number || prev.saIdNumber,
            institution: data.institution_name || prev.institution,
            studentNumber: data.student_number || prev.studentNumber,
            studyYear: data.study_year || prev.studyYear,
            nsfasMonthlyAllowance: data.nsfas_monthly_allowance != null
              ? parseFloat(data.nsfas_monthly_allowance)
              : prev.nsfasMonthlyAllowance,
            hasOtherCredit: data.has_other_credit ?? prev.hasOtherCredit,
            otherCreditMonthly: data.other_credit_monthly != null
              ? parseFloat(data.other_credit_monthly)
              : prev.otherCreditMonthly,
          } : null);

          if (data.bank_name || data.bank_account_number || data.bank_branch_code) {
            setSelectedApplication(prev => prev ? {
              ...prev,
              bankDetails: {
                bankName: data.bank_name || prev.bankDetails?.bankName,
                accountNumber: data.bank_account_number || prev.bankDetails?.accountNumber,
                branchCode: data.bank_branch_code || prev.bankDetails?.branchCode,
                accountHolder:
                  data.account_holder_name ||
                  `${data.first_name || ''} ${data.last_name || ''}`.trim() ||
                  prev.bankDetails?.accountHolder,
              },
            } : null);
          }
        } else {
          setApplicationDocuments([]);
        }
      } catch (error) {
        console.error('Error fetching application details:', error);
        setApplicationDocuments([]);
      } finally {
        setLoadingDocuments(false);
      }
    };

    fetchApplicationDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedApplication?.id]); // Only depend on ID to avoid infinite loop

  const handleDocumentReview = (applicationId: string) => {
    const app = applications.find(app => app.id === applicationId);
    setSelectedApplication(app || null);
    setShowDocumentReview(true);
  };

  const handleDocumentReviewComplete = async (applicationId: string, status: 'approved' | 'rejected', comments: string) => {
    try {
      setIsProcessing(true);
      
      console.log('🔄 Processing application:', {
        applicationId,
        status,
        action: status === 'approved' ? 'approve' : 'reject'
      });
      
      // Process the application based on document review
      const response = await adminAPI.processApplication(applicationId, {
        action: status === 'approved' ? 'approve' : 'reject',
        comments: comments || undefined,
        rejectionReason: status === 'rejected' ? (comments || 'Document verification failed') : undefined
      });

      console.log('📦 API Response:', response);

      if (response.success) {
        // Show success message
        alert(`✅ Application ${status === 'approved' ? 'approved' : 'rejected'} successfully!${status === 'approved' ? '\n\nNext step: Disburse the loan to release funds and create the repayment schedule.' : ''}`);
        
        // Refresh applications list
        const refreshResponse = await adminAPI.getApplicationsQueue({
          status: filterStatus === 'all' ? undefined : filterStatus,
          page: 1,
          limit: 50
        });
        
        if (refreshResponse.success) {
          setApplications(refreshResponse.data.applications);
        }
        
        // Close the modal
        setShowDocumentReview(false);
        setSelectedApplication(null);
      } else {
        const errorMessage = response.message || 'Unknown error';
        const errorDetails = (response as any).details ? `\n\nDetails: ${JSON.stringify((response as any).details)}` : '';
        console.error('❌ Failed to process application:', response);
        alert(`❌ Failed to ${status === 'approved' ? 'approve' : 'reject'} application\n\nError: ${errorMessage}${errorDetails}`);
      }
    } catch (error: any) {
      console.error('❌ Error processing application:', error);
      
      // Extract detailed error information
      let errorMessage = 'Unknown error occurred';
      let errorDetails = '';
      
      if (error.response) {
        // API responded with error
        errorMessage = error.response.data?.message || error.response.data?.error || `HTTP ${error.response.status}`;
        errorDetails = error.response.data?.details 
          ? `\n\nDetails: ${JSON.stringify(error.response.data.details)}`
          : error.response.data?.error 
          ? `\n\nError: ${error.response.data.error}`
          : '';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      if (error.code === 'ERR_NETWORK') {
        errorMessage = 'Network error - Backend server may not be running';
        errorDetails = '\n\nPlease check that the backend is running on http://localhost:3001';
      }
      
      alert(`❌ Error processing application\n\n${errorMessage}${errorDetails}\n\nPlease check:\n1. All documents are verified\n2. Backend server is running\n3. No pending or rejected documents`);
    } finally {
      setIsProcessing(false);
    }
  };

  const confirmAction = async () => {
    if (!selectedApplication) return;
    
    setIsProcessing(true);
    setError(''); // Clear previous errors
    
    try {
      console.log('🔄 Processing application:', {
        applicationId: selectedApplication.id,
        action: actionType,
        hasComments: !!commentText
      });
      
      const response = await adminAPI.processApplication(selectedApplication.id, {
        action: actionType,
        comments: commentText || undefined,
        rejectionReason: actionType === 'reject' ? commentText || undefined : undefined
      });
      
      console.log('📦 API Response:', response);
      
      if (response.success) {
        // Show success message
        alert(`✅ Application ${actionType === 'approve' ? 'approved' : actionType === 'reject' ? 'rejected' : 'updated'} successfully!${actionType === 'approve' ? '\n\nNext step: Disburse the loan to release funds and create the repayment schedule.' : ''}`);
        
        // Refresh applications list
        const refreshResponse = await adminAPI.getApplicationsQueue({
          status: filterStatus === 'all' ? undefined : filterStatus,
          page: 1,
          limit: 50
        });
        
        if (refreshResponse.success) {
          setApplications(refreshResponse.data.applications);
        }
        
        setShowCommentModal(false);
        setCommentText('');
        setSelectedApplication(null);
        setError('');
      } else {
        const errorMessage = response.message || 'Unknown error';
        const errorDetails = (response as any).details ? `\n\nDetails: ${JSON.stringify((response as any).details)}` : '';
        console.error('❌ Failed to process application:', response);
        const fullError = `Failed to ${actionType} application: ${errorMessage}${errorDetails}`;
        setError(fullError);
        alert(`❌ ${fullError}`);
      }
    } catch (err: any) {
      console.error('❌ Error processing application:', err);
      
      // Extract detailed error information
      let errorMessage = 'Unknown error occurred';
      let errorDetails = '';
      
      if (err.response) {
        // API responded with error
        errorMessage = err.response.data?.message || err.response.data?.error || `HTTP ${err.response.status}`;
        errorDetails = err.response.data?.details 
          ? `\n\nDetails: ${JSON.stringify(err.response.data.details)}`
          : err.response.data?.error 
          ? `\n\nError: ${err.response.data.error}`
          : '';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      if (err.code === 'ERR_NETWORK') {
        errorMessage = 'Network error - Backend server may not be running';
        errorDetails = '\n\nPlease check that the backend is running on http://localhost:3001';
      }
      
      const fullError = `Error processing application: ${errorMessage}${errorDetails}`;
      setError(fullError);
      alert(`❌ ${fullError}\n\nPlease check:\n1. All documents are verified (if approving)\n2. Backend server is running\n3. No pending or rejected documents`);
    } finally {
      setIsProcessing(false);
    }
  };

  const cancelAction = () => {
    setShowCommentModal(false);
    setCommentText('');
    setSelectedApplication(null);
  };

  const openDisburseModal = (application: Application) => {
    setDisburseApp(application);
    setDisburseReference('');
    setDisburseNotes('');
    setDisburseConfirmed(false);
    setShowDisburseModal(true);

    adminAPI
      .getApplicationDetails(application.id)
      .then((response) => {
        if (!response.success || !response.data) return;
        const data = response.data as any;
        setDisburseApp((prev) =>
          prev
            ? {
                ...prev,
                bankDetails: {
                  bankName: data.bank_name || prev.bankDetails?.bankName,
                  accountNumber: data.bank_account_number || prev.bankDetails?.accountNumber,
                  branchCode: data.bank_branch_code || prev.bankDetails?.branchCode,
                  accountHolder:
                    data.account_holder_name ||
                    `${data.first_name || ''} ${data.last_name || ''}`.trim() ||
                    prev.bankDetails?.accountHolder,
                },
              }
            : prev
        );
      })
      .catch(() => undefined);
  };

  const confirmDisburse = async () => {
    if (!disburseApp || !disburseConfirmed) return;
    setDisbursing(true);
    try {
      const result = await adminAPI.disburseLoan(disburseApp.id, {
        disbursementMethod: 'manual_eft',
        disbursementReference: disburseReference.trim() || undefined,
        notes: disburseNotes.trim() || undefined,
      });
      if (result.success) {
        alert(
          `Marked as paid out.\n\nAmount: R${result.data.amountDisbursed.toFixed(2)}\nStudent: ${result.data.studentName}\nReference: ${result.data.disbursementReference || '—'}\n\nThe student has been notified.`
        );
        setShowDisburseModal(false);
        setDisburseApp(null);
        const refreshResponse = await adminAPI.getApplicationsQueue({
          status: filterStatus === 'all' ? undefined : filterStatus,
          page: 1,
          limit: 50,
        });
        if (refreshResponse.success) {
          setApplications(refreshResponse.data.applications);
        }
      } else {
        alert(`Failed to mark as paid out: ${result.message}`);
      }
    } catch (err: any) {
      console.error('Disbursement error:', err);
      alert(
        err.response?.data?.message || err.message || 'Failed to mark loan as paid out'
      );
    } finally {
      setDisbursing(false);
    }
  };

  // Filter and sort applications
  const filteredApplications = applications
    .filter(app => filterStatus === 'all' || app.status === filterStatus)
    .sort((a, b) => {
      switch (sortBy) {
        case 'amount':
          return b.amount - a.amount;
        case 'risk':
          return b.riskScore - a.riskScore;
        case 'date':
        default:
          return new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime();
      }
    });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return '#2ECC40';
      case 'rejected': return '#FF4136';
      case 'under_review': return '#EA580C';
      case 'pending': return '#0074D9';
      case 'requires_info': return '#fd7e14';
      default: return '#64748B';
    }
  };

  const getRiskColor = (score: number) => {
    // Higher score = Higher risk = Red colors
    // Lower score = Lower risk = Green colors
    if (score >= 80) return '#FF4136'; // Red - Very High risk (80-100)
    if (score >= 60) return '#fd7e14'; // Orange - High risk (60-79)
    if (score >= 40) return '#EA580C'; // Yellow - Medium risk (40-59)
    return '#2ECC40'; // Green - Low risk (0-39)
  };

  const getRiskLevelLabel = (score: number): string => {
    if (score >= 80) return 'Very High';
    if (score >= 60) return 'High';
    if (score >= 40) return 'Medium';
    return 'Low';
  };

  const getRiskIcon = (score: number) => {
    if (score >= 80) {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
          <line x1="12" y1="9" x2="12" y2="13"/>
          <line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>
      );
    } else if (score >= 60) {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
          <line x1="12" y1="9" x2="12" y2="13"/>
        </svg>
      );
    } else if (score >= 40) {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="8" x2="12" y2="12"/>
          <line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
      );
    } else {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
          <polyline points="22,4 12,14.01 9,11.01"/>
        </svg>
      );
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="applications-queue">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading applications...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="applications-queue">
        <div className="error-state">
          <p>Error: {error}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="applications-queue">
      {/* Header */}
      <div className="queue-header">
        <h2 className="queue-title">Loan Applications</h2>
        <p className="queue-subtitle">
          Review and manage pending loan applications
        </p>
      </div>

      {/* Filters and Controls */}
      <div className="queue-controls">
        <div className="controls-left">
          <div className="filter-group">
            <label htmlFor="status-filter" className="filter-label">Status:</label>
            <select
              id="status-filter"
              className="filter-select"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">All Applications</option>
              <option value="pending">Pending</option>
              <option value="under_review">Under Review</option>
              <option value="requires_info">Requires Info</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="sort-select" className="filter-label">Sort by:</label>
            <select
              id="sort-select"
              className="filter-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'date' | 'amount' | 'risk')}
            >
              <option value="date">Date Submitted</option>
              <option value="amount">Loan Amount</option>
              <option value="risk">Risk Score</option>
            </select>
          </div>
        </div>

        <div className="controls-right">
          <span className="results-count">
            {filteredApplications.length} applications
          </span>
        </div>
      </div>

      {/* Applications List */}
      <div className="applications-list">
        {filteredApplications.map((application) => (
          <div
            key={application.id}
            className={`application-card ${selectedApplication?.id === application.id ? 'selected' : ''}`}
            onClick={() => setSelectedApplication(application)}
          >
            <div className="application-header">
              <div className="applicant-info">
                <h3 className="applicant-name">{application.applicantName}</h3>
                <span className="application-id">
                  {application.saIdNumber ? `ID ${application.saIdNumber}` : application.id}
                </span>
              </div>
              
              <div className="application-status">
                <span
                  className="status-badge"
                  style={{ backgroundColor: getStatusColor(application.status) }}
                >
                  {application.status.replace('_', ' ').toUpperCase()}
                </span>
              </div>
            </div>

            <div className="application-details">
              <div className="detail-row">
                <div className="detail-item">
                  <span className="detail-label">Amount:</span>
                  <span className="detail-value">R{application.amount}</span>
                </div>
                
                <div className="detail-item">
                  <span className="detail-label">Total Repayment:</span>
                  <span className="detail-value">R{application.totalRepayment}</span>
                </div>
                
                <div className="detail-item">
                  <span className="detail-label">Risk Score:</span>
                  <span
                    className="detail-value risk-score"
                    style={{ color: getRiskColor(application.riskScore) }}
                  >
                    {application.riskScore}%
                  </span>
                </div>
              </div>

              <div className="detail-row">
                <div className="detail-item">
                  <span className="detail-label">Institution:</span>
                  <span className="detail-value" style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '120px'}}>{application.institution}</span>
                </div>
                
                <div className="detail-item">
                  <span className="detail-label">Submitted:</span>
                  <span className="detail-value">{formatDate(application.submittedAt)}</span>
                </div>
              </div>

              <div className="verification-status">
                <div className="verification-item">
                  <span className={`verification-badge ${application.documentsComplete ? 'complete' : 'incomplete'}`}>
                    {application.documentsComplete ? '✓' : '✗'} Documents
                  </span>
                </div>
                
                <div className="verification-item">
                  <span className={`verification-badge ${application.nsfasVerified ? 'complete' : 'incomplete'}`}>
                    {application.nsfasVerified ? '✓' : '✗'} NSFAS
                  </span>
                </div>
              </div>
            </div>

            <div className="application-actions">
              {(application.status === 'pending' || application.status === 'under_review') ? (
                <>
                  <button
                    className="action-btn approve-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleApplicationAction('approve', application.id);
                    }}
                    type="button"
                    disabled={isProcessing}
                  >
                    ✓ Approve
                  </button>
                  
                  <button
                    className="action-btn reject-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleApplicationAction('reject', application.id);
                    }}
                    type="button"
                    disabled={isProcessing}
                  >
                    ✗ Reject
                  </button>
                </>
              ) : application.status === 'approved' ? (
                 /* Disburse button for approved applications */
                 <button
                   className="action-btn disburse-btn"
                   onClick={(e) => {
                     e.stopPropagation();
                     openDisburseModal(application);
                   }}
                   type="button"
                   disabled={isProcessing}
                   style={{
                     background: 'linear-gradient(135deg, #2ECC40 0%, #2ECC40 100%)',
                     color: 'white',
                     border: 'none',
                     padding: '0.5rem 1rem',
                     borderRadius: '6px',
                     fontWeight: '600',
                     cursor: 'pointer'
                   }}
                 >
                   Mark as Paid Out
                 </button>
              ) : (
                 /* Status indicator for rejected/other apps */
                 <span style={{fontSize: '0.85rem', color: '#64748b', fontStyle: 'italic', marginRight: 'auto', display: 'flex', alignItems: 'center'}}>
                   {application.status === 'rejected' ? '✗ Rejected' : 'ℹ Needs Info'}
                 </span>
              )}
              
              <button
                className="action-btn review-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedApplication(application);
                }}
                type="button"
              >
                📋 View Details
              </button>
            </div>
          </div>
        ))}

        {filteredApplications.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14,2 14,8 20,8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
              </svg>
            </div>
            <h3 className="empty-title">No Applications Found</h3>
            <p className="empty-description">
              No applications match your current filters.
            </p>
          </div>
        )}
      </div>

      {/* Application Detail Modal */}
      {selectedApplication && (
        <div className="application-modal-overlay" onClick={() => setSelectedApplication(null)}>
          <div className="application-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="applicant-header-info">
                <h3 className="applicant-name">{selectedApplication.applicantName}</h3>
                <p className="applicant-id">{selectedApplication.id}</p>
              </div>
              <div className="header-right">
                <span className={`status-badge ${selectedApplication.status}`}>
                  {selectedApplication.status.toUpperCase()}
                </span>
                <button
                  className="modal-close"
                  onClick={() => setSelectedApplication(null)}
                  type="button"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="modal-content">
              {/* Enhanced Applicant Information */}
              <div className="modal-section">
                <h4 className="section-title">
                  <svg className="section-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                  Applicant Information
                </h4>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">Full Name:</span>
                    <span className="info-value">{selectedApplication.applicantName}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">SA ID number:</span>
                    <span className="info-value">{selectedApplication.saIdNumber || '—'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Phone:</span>
                    <span className="info-value">{selectedApplication.phone}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Email:</span>
                    <span className="info-value">{selectedApplication.email}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Institution:</span>
                    <span className="info-value">{selectedApplication.institution}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Student Number:</span>
                    <span className="info-value">{selectedApplication.studentNumber}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Year of study:</span>
                    <span className="info-value">{selectedApplication.studyYear || '—'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Monthly NSFAS allowance:</span>
                    <span className="info-value">
                      {selectedApplication.nsfasMonthlyAllowance != null
                        ? `R${selectedApplication.nsfasMonthlyAllowance.toFixed(2)}`
                        : '—'}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Other credit repayments:</span>
                    <span className="info-value">
                      {selectedApplication.hasOtherCredit == null
                        ? '—'
                        : selectedApplication.hasOtherCredit
                          ? `Yes — R${(selectedApplication.otherCreditMonthly || 0).toFixed(2)} / month`
                          : 'No'}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Application ID:</span>
                    <span className="info-value" style={{fontFamily: 'Courier New, monospace', fontSize: '0.85rem'}}>{selectedApplication.id}</span>
                  </div>
                </div>
              </div>

              <div className="modal-section">
                <h4 className="section-title">Bank Details (for payout)</h4>
                {selectedApplication.bankDetails?.accountNumber ? (
                  <div className="info-grid">
                    <div className="info-item">
                      <span className="info-label">Bank:</span>
                      <span className="info-value">{selectedApplication.bankDetails.bankName || '—'}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Account Holder:</span>
                      <span className="info-value">
                        {selectedApplication.bankDetails.accountHolder || selectedApplication.applicantName}
                      </span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Account Number:</span>
                      <span className="info-value" style={{ fontFamily: 'monospace' }}>
                        {selectedApplication.bankDetails.accountNumber}
                      </span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Branch Code:</span>
                      <span className="info-value" style={{ fontFamily: 'monospace' }}>
                        {selectedApplication.bankDetails.branchCode || '—'}
                      </span>
                    </div>
                  </div>
                ) : (
                  <p className="info-note" style={{ margin: 0 }}>
                    No bank details on file. Ask the student to update their profile before paying out.
                  </p>
                )}
              </div>

              {/* Application Timeline */}
              <div className="modal-section">
                <h4 className="section-title">
                  <svg className="section-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12,6 12,12 16,14"/>
                  </svg>
                  Application Timeline
                </h4>
                <div className="timeline-container">
                  <div className="timeline-item">
                    <div className="timeline-marker submitted"></div>
                    <div className="timeline-content">
                      <span className="timeline-title">Application Submitted</span>
                      <span className="timeline-date">{formatDate(selectedApplication.submittedAt)}</span>
                    </div>
                  </div>
                  {selectedApplication.reviewedAt && (
                    <div className="timeline-item">
                      <div className="timeline-marker reviewed"></div>
                      <div className="timeline-content">
                        <span className="timeline-title">Under Review</span>
                        <span className="timeline-date">{formatDate(selectedApplication.reviewedAt)}</span>
                        {selectedApplication.reviewedBy && (
                          <span className="timeline-detail">by {selectedApplication.reviewedBy}</span>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="timeline-item">
                    <div className={`timeline-marker ${selectedApplication.status}`}></div>
                    <div className="timeline-content">
                      <span className="timeline-title">Current Status: {selectedApplication.status.replace('_', ' ').toUpperCase()}</span>
                      <span className="timeline-date">{formatDate(selectedApplication.lastUpdated)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-section">
                <h4 className="section-title">
                  <svg className="section-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="1" x2="12" y2="23"/>
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                  </svg>
                  Loan Details
                </h4>
                <div className="loan-details-grid">
                  <div className="loan-summary-card">
                    <div className="loan-amount">
                      <span className="amount-label">Requested Amount</span>
                      <span className="amount-value">R{selectedApplication.amount.toLocaleString()}</span>
                    </div>
                    <div className="loan-breakdown">
                      <div className="breakdown-item">
                        <span className="breakdown-label">Principal:</span>
                        <span className="breakdown-value">R{selectedApplication.amount.toLocaleString()}</span>
                      </div>
                      <div className="breakdown-item">
                        <span className="breakdown-label">Interest (5%):</span>
                        <span className="breakdown-value">R{(selectedApplication.amount * 0.05).toLocaleString()}</span>
                      </div>
                      <div className="breakdown-item">
                        <span className="breakdown-label">Admin Fee:</span>
                        <span className="breakdown-value">R{(selectedApplication.amount * 0.1).toLocaleString()}</span>
                      </div>
                      <div className="breakdown-item total">
                        <span className="breakdown-label">Total Repayment:</span>
                        <span className="breakdown-value">R{selectedApplication.totalRepayment.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="risk-assessment-card">
                    <div className="risk-header">
                      <span className="risk-title">
                        <svg className="risk-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                          <path d="M2 17l10 5 10-5"/>
                          <path d="M2 12l10 5 10-5"/>
                        </svg>
                        Risk Assessment
                      </span>
                      <span 
                        className="risk-score-badge"
                        style={{ 
                          backgroundColor: getRiskColor(selectedApplication.riskScore),
                          color: 'white',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '20px',
                          fontSize: '0.8rem',
                          fontWeight: '600'
                        }}
                      >
                        {selectedApplication.riskScore}% Risk
                      </span>
                    </div>
                    {selectedApplication.riskAssessment ? (
                      <div className="risk-breakdown-detailed">
                        <div className="risk-level-badge" style={{
                          backgroundColor: getRiskColor(selectedApplication.riskAssessment.overallScore),
                          color: 'white',
                          padding: '0.5rem 1rem',
                          borderRadius: '8px',
                          fontSize: '0.9rem',
                          fontWeight: '600',
                          marginBottom: '1rem',
                          textAlign: 'center'
                        }}>
                          {selectedApplication.riskAssessment.riskLevel.toUpperCase()} RISK
                        </div>
                        
                        <div className="risk-breakdown">
                          <div className="risk-item">
                            <div className="risk-item-header">
                              <div className="risk-label-group">
                                <span className="risk-label">NSFAS Verification</span>
                                <span className="risk-label-hint">Status: {selectedApplication.nsfasVerified ? 'Verified ✓' : 'Not Verified ✗'}</span>
                              </div>
                              <div className="risk-score-badge" style={{ 
                                backgroundColor: getRiskColor(selectedApplication.riskAssessment.breakdown.nsfasVerification),
                                color: 'white'
                              }}>
                                {getRiskIcon(selectedApplication.riskAssessment.breakdown.nsfasVerification)}
                                <span className="risk-score-text">
                                  {getRiskLevelLabel(selectedApplication.riskAssessment.breakdown.nsfasVerification)} Risk
                                </span>
                                <span className="risk-score-percentage">
                                  ({Math.round(selectedApplication.riskAssessment.breakdown.nsfasVerification)}%)
                                </span>
                              </div>
                            </div>
                            <div className="risk-bar">
                              <div 
                                className="risk-bar-fill" 
                                style={{ 
                                  width: `${selectedApplication.riskAssessment.breakdown.nsfasVerification}%`,
                                  backgroundColor: getRiskColor(selectedApplication.riskAssessment.breakdown.nsfasVerification)
                                }}
                              ></div>
                            </div>
                          </div>
                          
                          <div className="risk-item">
                            <div className="risk-item-header">
                              <div className="risk-label-group">
                                <span className="risk-label">Document Completeness</span>
                                <span className="risk-label-hint">Status: {selectedApplication.documentsComplete ? 'Complete ✓' : 'Incomplete ✗'}</span>
                              </div>
                              <div className="risk-score-badge" style={{ 
                                backgroundColor: getRiskColor(selectedApplication.riskAssessment.breakdown.documentCompleteness),
                                color: 'white'
                              }}>
                                {getRiskIcon(selectedApplication.riskAssessment.breakdown.documentCompleteness)}
                                <span className="risk-score-text">
                                  {getRiskLevelLabel(selectedApplication.riskAssessment.breakdown.documentCompleteness)} Risk
                                </span>
                                <span className="risk-score-percentage">
                                  ({Math.round(selectedApplication.riskAssessment.breakdown.documentCompleteness)}%)
                                </span>
                              </div>
                            </div>
                            <div className="risk-bar">
                              <div 
                                className="risk-bar-fill" 
                                style={{ 
                                  width: `${selectedApplication.riskAssessment.breakdown.documentCompleteness}%`,
                                  backgroundColor: getRiskColor(selectedApplication.riskAssessment.breakdown.documentCompleteness)
                                }}
                              ></div>
                            </div>
                          </div>
                          
                          <div className="risk-item">
                            <div className="risk-item-header">
                              <div className="risk-label-group">
                                <span className="risk-label">Loan Amount</span>
                                <span className="risk-label-hint">Amount: R{selectedApplication.amount.toLocaleString()}</span>
                              </div>
                              <div className="risk-score-badge" style={{ 
                                backgroundColor: getRiskColor(selectedApplication.riskAssessment.breakdown.loanAmount),
                                color: 'white'
                              }}>
                                {getRiskIcon(selectedApplication.riskAssessment.breakdown.loanAmount)}
                                <span className="risk-score-text">
                                  {getRiskLevelLabel(selectedApplication.riskAssessment.breakdown.loanAmount)} Risk
                                </span>
                                <span className="risk-score-percentage">
                                  ({Math.round(selectedApplication.riskAssessment.breakdown.loanAmount)}%)
                                </span>
                              </div>
                            </div>
                            <div className="risk-bar">
                              <div 
                                className="risk-bar-fill" 
                                style={{ 
                                  width: `${selectedApplication.riskAssessment.breakdown.loanAmount}%`,
                                  backgroundColor: getRiskColor(selectedApplication.riskAssessment.breakdown.loanAmount)
                                }}
                              ></div>
                            </div>
                          </div>
                          
                          <div className="risk-item">
                            <div className="risk-item-header">
                              <div className="risk-label-group">
                                <span className="risk-label">Payment History</span>
                                <span className="risk-label-hint">Based on previous loan repayments</span>
                              </div>
                              <div className="risk-score-badge" style={{ 
                                backgroundColor: getRiskColor(selectedApplication.riskAssessment.breakdown.paymentHistory),
                                color: 'white'
                              }}>
                                {getRiskIcon(selectedApplication.riskAssessment.breakdown.paymentHistory)}
                                <span className="risk-score-text">
                                  {getRiskLevelLabel(selectedApplication.riskAssessment.breakdown.paymentHistory)} Risk
                                </span>
                                <span className="risk-score-percentage">
                                  ({Math.round(selectedApplication.riskAssessment.breakdown.paymentHistory)}%)
                                </span>
                              </div>
                            </div>
                            <div className="risk-bar">
                              <div 
                                className="risk-bar-fill" 
                                style={{ 
                                  width: `${selectedApplication.riskAssessment.breakdown.paymentHistory}%`,
                                  backgroundColor: getRiskColor(selectedApplication.riskAssessment.breakdown.paymentHistory)
                                }}
                              ></div>
                            </div>
                          </div>
                          
                          <div className="risk-item">
                            <div className="risk-item-header">
                              <div className="risk-label-group">
                                <span className="risk-label">Account History</span>
                                <span className="risk-label-hint">Account age and status</span>
                              </div>
                              <div className="risk-score-badge" style={{ 
                                backgroundColor: getRiskColor(selectedApplication.riskAssessment.breakdown.accountHistory),
                                color: 'white'
                              }}>
                                {getRiskIcon(selectedApplication.riskAssessment.breakdown.accountHistory)}
                                <span className="risk-score-text">
                                  {getRiskLevelLabel(selectedApplication.riskAssessment.breakdown.accountHistory)} Risk
                                </span>
                                <span className="risk-score-percentage">
                                  ({Math.round(selectedApplication.riskAssessment.breakdown.accountHistory)}%)
                                </span>
                              </div>
                            </div>
                            <div className="risk-bar">
                              <div 
                                className="risk-bar-fill" 
                                style={{ 
                                  width: `${selectedApplication.riskAssessment.breakdown.accountHistory}%`,
                                  backgroundColor: getRiskColor(selectedApplication.riskAssessment.breakdown.accountHistory)
                                }}
                              ></div>
                            </div>
                          </div>
                          
                          <div className="risk-item">
                            <div className="risk-item-header">
                              <div className="risk-label-group">
                                <span className="risk-label">Application Pattern</span>
                                <span className="risk-label-hint">Frequency of recent applications</span>
                              </div>
                              <div className="risk-score-badge" style={{ 
                                backgroundColor: getRiskColor(selectedApplication.riskAssessment.breakdown.applicationPattern),
                                color: 'white'
                              }}>
                                {getRiskIcon(selectedApplication.riskAssessment.breakdown.applicationPattern)}
                                <span className="risk-score-text">
                                  {getRiskLevelLabel(selectedApplication.riskAssessment.breakdown.applicationPattern)} Risk
                                </span>
                                <span className="risk-score-percentage">
                                  ({Math.round(selectedApplication.riskAssessment.breakdown.applicationPattern)}%)
                                </span>
                              </div>
                            </div>
                            <div className="risk-bar">
                              <div 
                                className="risk-bar-fill" 
                                style={{ 
                                  width: `${selectedApplication.riskAssessment.breakdown.applicationPattern}%`,
                                  backgroundColor: getRiskColor(selectedApplication.riskAssessment.breakdown.applicationPattern)
                                }}
                              ></div>
                            </div>
                          </div>
                        </div>
                        
                        {selectedApplication.riskAssessment.recommendations.length > 0 && (
                          <div className="risk-recommendations">
                            <h5 className="recommendations-title">Recommendations:</h5>
                            <ul className="recommendations-list">
                              {selectedApplication.riskAssessment.recommendations.map((rec, index) => (
                                <li key={index} className="recommendation-item">
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"/>
                                    <line x1="12" y1="8" x2="12" y2="12"/>
                                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                                  </svg>
                                  {rec}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="risk-breakdown">
                        <div className="risk-item">
                          <span className="risk-label">NSFAS Verification:</span>
                          <span className={`risk-status ${selectedApplication.nsfasVerified ? 'verified' : 'pending'}`}>
                            {selectedApplication.nsfasVerified ? 'Verified' : 'Pending'}
                          </span>
                        </div>
                        <div className="risk-item">
                          <span className="risk-label">Documentation:</span>
                          <span className={`risk-status ${selectedApplication.documentsComplete ? 'verified' : 'pending'}`}>
                            {selectedApplication.documentsComplete ? 'Complete' : 'Incomplete'}
                          </span>
                        </div>
                        <div className="risk-loading">Loading detailed risk assessment...</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Uploaded Documents Section */}
              <div className="modal-section">
                <h4 className="section-title">
                  <svg className="section-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14,2 14,8 20,8"/>
                  </svg>
                  Uploaded Documents
                </h4>
                {loadingDocuments ? (
                  <div className="documents-loading">
                    <div className="loading-spinner-small"></div>
                    <span>Loading documents...</span>
                  </div>
                ) : applicationDocuments.length > 0 ? (
                  <div className="uploaded-documents-list">
                    {applicationDocuments.map((doc: any) => {
                      const getDocumentTypeName = (type: string) => {
                        switch (type) {
                          case 'nsfas_statement': return 'NSFAS Statement';
                          case 'bank_statement': return 'Bank Statement';
                          case 'student_card': return 'Student Card';
                          case 'id_document': return 'ID Document';
                          default: return type.replace('_', ' ');
                        }
                      };

                      const getStatusColor = (status: string) => {
                        switch (status) {
                          case 'verified': return '#2ECC40';
                          case 'rejected': return '#FF4136';
                          default: return '#EA580C';
                        }
                      };

                      return (
                        <div key={doc.id} className="uploaded-document-item">
                          <div className="document-item-left">
                            <div className="document-icon">
                              {doc.document_type === 'student_card' || doc.document_type === 'id_document' ? (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                                  <circle cx="8.5" cy="8.5" r="1.5"/>
                                  <polyline points="21,15 16,10 5,21"/>
                                </svg>
                              ) : (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                  <polyline points="14,2 14,8 20,8"/>
                                </svg>
                              )}
                            </div>
                            <div className="document-info">
                              <span className="document-type-name">{getDocumentTypeName(doc.document_type)}</span>
                              <span className="document-file-name">
                                {displayDocumentName(
                                  doc.document_name,
                                  doc.mime_type,
                                  doc.document_type
                                )}
                              </span>
                              <span className="document-size">
                                {formatMimeLabel(doc.mime_type, doc.document_name)}
                                {doc.file_size
                                  ? ` · ${(doc.file_size / 1024 / 1024).toFixed(2)} MB`
                                  : ''}
                              </span>
                            </div>
                          </div>
                          <div className="document-item-right">
                            <span 
                              className="document-status-badge"
                              style={{ 
                                backgroundColor: getStatusColor(doc.verification_status),
                                color: 'white'
                              }}
                            >
                              {doc.verification_status === 'verified' ? '✓ Verified' : 
                               doc.verification_status === 'rejected' ? '✗ Rejected' : 
                               '⏳ Pending'}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="no-documents-message">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ color: '#9ca3af', marginBottom: '0.5rem' }}>
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14,2 14,8 20,8"/>
                    </svg>
                    <p>No documents uploaded yet</p>
                  </div>
                )}
              </div>

              {/* NSFAS Verification Status */}
              <div className="modal-section">
                <h4 className="section-title">
                  <svg className="section-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
                  </svg>
                  NSFAS Verification Status
                </h4>
                <div className="nsfas-status-card">
                  <div className="nsfas-status-header">
                    <div className="nsfas-status-info">
                      <span className="nsfas-status-label">Verification Status:</span>
                      <span className={`nsfas-status-value ${selectedApplication.nsfasVerified ? 'verified' : 'pending'}`}>
                        {selectedApplication.nsfasVerified ? (
                          <>
                            <svg className="status-icon verified" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                              <polyline points="22,4 12,14.01 9,11.01"/>
                            </svg>
                            Verified
                          </>
                        ) : (
                          <>
                            <svg className="status-icon pending" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="12" r="10"/>
                              <polyline points="12,6 12,12 16,14"/>
                            </svg>
                            Pending Verification
                          </>
                        )}
                      </span>
                    </div>
                  </div>
                  {!selectedApplication.nsfasVerified && (
                    <div className="nsfas-status-note">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: '#EA580C' }}>
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="12" y1="16" x2="12" y2="12"/>
                        <line x1="12" y1="8" x2="12.01" y2="8"/>
                      </svg>
                      <span>NSFAS beneficiary status needs to be verified before approval</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Comments and Rejection Reasons */}
              {(selectedApplication.comments || selectedApplication.rejectionReason) && (
                <div className="modal-section">
                  <h4 className="section-title">
                    <svg className="section-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14,2 14,8 20,8"/>
                      <line x1="16" y1="13" x2="8" y2="13"/>
                      <line x1="16" y1="17" x2="8" y2="17"/>
                    </svg>
                    Review Notes
                  </h4>
                  <div className="notes-container">
                    {selectedApplication.comments && (
                      <div className="note-item">
                        <span className="note-label">
                          <svg className="note-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                          </svg>
                          Comments:
                        </span>
                        <div className="note-content">{selectedApplication.comments}</div>
                      </div>
                    )}
                    {selectedApplication.rejectionReason && (
                      <div className="note-item">
                        <span className="note-label">
                          <svg className="note-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="15" y1="9" x2="9" y2="15"/>
                            <line x1="9" y1="9" x2="15" y2="15"/>
                          </svg>
                          Rejection Reason:
                        </span>
                        <div className="note-content rejection">{selectedApplication.rejectionReason}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="modal-actions">
              {(selectedApplication.status === 'pending' || selectedApplication.status === 'under_review') && (
                <>
                  <button
                    className="modal-btn document-btn"
                    onClick={() => {
                      handleDocumentReview(selectedApplication.id);
                    }}
                    type="button"
                    disabled={isProcessing}
                  >
                    📄 Review Documents
                  </button>
                  
                  <button
                    className="modal-btn approve-btn"
                    onClick={() => {
                      handleApplicationAction('approve', selectedApplication.id);
                    }}
                    type="button"
                    disabled={isProcessing}
                  >
                    ✓ Approve Application
                  </button>
                  
                  <button
                    className="modal-btn reject-btn"
                    onClick={() => {
                      handleApplicationAction('reject', selectedApplication.id);
                    }}
                    type="button"
                    disabled={isProcessing}
                  >
                    ✗ Reject Application
                  </button>
                  
                  <button
                    className="modal-btn info-btn"
                    onClick={() => {
                      handleApplicationAction('request_info', selectedApplication.id);
                    }}
                    type="button"
                    disabled={isProcessing}
                  >
                    ℹ Request More Info
                  </button>
                </>
              )}
              
              <button
                className="modal-btn secondary-btn"
                onClick={() => setSelectedApplication(null)}
                type="button"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Comment Modal */}
      {showCommentModal && selectedApplication && (
        <div 
          className="application-modal-overlay" 
          style={{ zIndex: 9999 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              cancelAction();
            }
          }}
        >
          <div className="application-modal comment-modal">
            <div className="modal-header">
              <h3 className="modal-title">
                {actionType === 'approve' && '✓ Approve Application'}
                {actionType === 'reject' && '✗ Reject Application'}
                {actionType === 'request_info' && 'ℹ Request More Information'}
              </h3>
              <button
                className="modal-close"
                onClick={cancelAction}
                type="button"
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="application-summary">
                <h4>{selectedApplication.applicantName}</h4>
                <p>Application ID: {selectedApplication.id}</p>
                <p>Amount: R{selectedApplication.amount}</p>
              </div>

              <div className="comment-section">
                <label htmlFor="comment-text" className="comment-label">
                  {actionType === 'approve' && 'Approval Notes (Optional):'}
                  {actionType === 'reject' && 'Rejection Reason (Required):'}
                  {actionType === 'request_info' && 'Information Request Details (Required):'}
                </label>
                <textarea
                  id="comment-text"
                  className="comment-textarea"
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder={
                    actionType === 'approve' 
                      ? 'Add any notes about this approval...'
                      : actionType === 'reject'
                      ? 'Please provide the reason for rejection...'
                      : 'Please specify what additional information is needed...'
                  }
                  rows={4}
                  required={actionType !== 'approve'}
                />
              </div>
            </div>

            <div className="modal-actions">
              <button
                className="modal-btn secondary-btn"
                onClick={cancelAction}
                type="button"
                disabled={isProcessing}
              >
                Cancel
              </button>
              
              <button
                className={`modal-btn ${
                  actionType === 'approve' ? 'approve-btn' :
                  actionType === 'reject' ? 'reject-btn' : 'info-btn'
                }`}
                onClick={confirmAction}
                type="button"
                disabled={isProcessing || (actionType !== 'approve' && !commentText.trim())}
              >
                {isProcessing ? 'Processing...' : (
                  <>
                    {actionType === 'approve' && '✓ Confirm Approval'}
                    {actionType === 'reject' && '✗ Confirm Rejection'}
                    {actionType === 'request_info' && 'ℹ Send Request'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document Review Modal */}
      {showDocumentReview && selectedApplication && (
        <div className="modal-overlay" onClick={() => setShowDocumentReview(false)}>
          <div className="modal-content document-review-modal" onClick={(e) => e.stopPropagation()}>
            <DocumentReview
              applicationId={selectedApplication.id}
              applicantName={selectedApplication.applicantName}
              onReviewComplete={handleDocumentReviewComplete}
            />
          </div>
        </div>
      )}

      {/* Manual EFT / Mark as Paid Out Modal */}
      {showDisburseModal && disburseApp && (
        <div
          className="application-modal-overlay"
          style={{ zIndex: 9999 }}
          onClick={(e) => {
            if (e.target === e.currentTarget && !disbursing) {
              setShowDisburseModal(false);
              setDisburseApp(null);
            }
          }}
        >
          <div className="application-modal comment-modal disburse-modal">
            <div className="modal-header">
              <h3 className="modal-title">Mark as Paid Out (Manual EFT)</h3>
              <button
                className="modal-close"
                type="button"
                disabled={disbursing}
                onClick={() => {
                  setShowDisburseModal(false);
                  setDisburseApp(null);
                }}
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="application-summary">
                <h4>{disburseApp.applicantName}</h4>
                <p>Pay <strong>R{disburseApp.amount.toFixed(2)}</strong> from GenLend&apos;s business bank account</p>
                <p style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '0.5rem' }}>
                  GenLend does not move money automatically. Transfer via your bank app first, then confirm here.
                </p>
              </div>

              <div className="disburse-bank-box">
                <h4>Pay to these bank details</h4>
                {disburseApp.bankDetails?.accountNumber ? (
                  <div className="info-grid">
                    <div className="info-item">
                      <span className="info-label">Bank</span>
                      <span className="info-value">{disburseApp.bankDetails.bankName || '—'}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Account holder</span>
                      <span className="info-value">
                        {disburseApp.bankDetails.accountHolder || disburseApp.applicantName}
                      </span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Account number</span>
                      <span className="info-value" style={{ fontFamily: 'monospace' }}>
                        {disburseApp.bankDetails.accountNumber}
                      </span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Branch code</span>
                      <span className="info-value" style={{ fontFamily: 'monospace' }}>
                        {disburseApp.bankDetails.branchCode || '—'}
                      </span>
                    </div>
                  </div>
                ) : (
                  <p className="disburse-warning">
                    Bank details missing. Do not pay out until the student has updated their profile.
                  </p>
                )}
              </div>

              <div className="comment-section">
                <label className="comment-label" htmlFor="disburse-reference">
                  Bank payment reference (optional)
                </label>
                <input
                  id="disburse-reference"
                  className="disburse-input"
                  type="text"
                  value={disburseReference}
                  onChange={(e) => setDisburseReference(e.target.value)}
                  placeholder="e.g. EFT / Instant Payment / PayShap reference"
                  disabled={disbursing}
                />
              </div>

              <div className="comment-section">
                <label className="comment-label" htmlFor="disburse-notes">
                  Notes (optional)
                </label>
                <textarea
                  id="disburse-notes"
                  className="comment-textarea"
                  rows={3}
                  value={disburseNotes}
                  onChange={(e) => setDisburseNotes(e.target.value)}
                  placeholder="Any admin notes about this payout…"
                  disabled={disbursing}
                />
              </div>

              <label className="disburse-confirm">
                <input
                  type="checkbox"
                  checked={disburseConfirmed}
                  onChange={(e) => setDisburseConfirmed(e.target.checked)}
                  disabled={disbursing || !disburseApp.bankDetails?.accountNumber}
                />
                <span>
                  I have already transferred R{disburseApp.amount.toFixed(2)} to the bank details above.
                </span>
              </label>
            </div>

            <div className="modal-actions">
              <button
                className="modal-btn secondary-btn"
                type="button"
                disabled={disbursing}
                onClick={() => {
                  setShowDisburseModal(false);
                  setDisburseApp(null);
                }}
              >
                Cancel
              </button>
              <button
                className="modal-btn approve-btn"
                type="button"
                disabled={
                  disbursing ||
                  !disburseConfirmed ||
                  !disburseApp.bankDetails?.accountNumber
                }
                onClick={confirmDisburse}
              >
                {disbursing ? 'Saving…' : 'Confirm paid out'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationsQueue;

